import React, { Component } from "react";
import { Card, CardBody, CardHeader, CardTitle, Col, Row } from "reactstrap";

class UpdatedvrResult extends Component {
  state = {
    stdOut: "",
  };

  static getDerivedStateFromProps(nextProps) {
    return {
      stdOut: nextProps.stdOut,
      status: nextProps.status,
      stdErr: nextProps.stdErr,
    };
  }

  // ========== JSON Parsing ==========
  parseJsonResult = (resultString) => {
    if (!resultString || typeof resultString !== "string") {
      return null;
    }

    const trimmedString = resultString.trim();

    // Try to parse as JSON first (for Hikvision/Dahua)
    try {
      const jsonResult = JSON.parse(trimmedString);
      if (
        jsonResult &&
        (jsonResult.data !== undefined ||
          jsonResult.before !== undefined ||
          jsonResult.after !== undefined ||
          jsonResult.operation !== undefined ||
          jsonResult.global_status !== undefined ||
          jsonResult.errors !== undefined)
      ) {
        return jsonResult;
      }
    } catch (e) {
      // Not valid JSON, try to extract JSON from text
      try {
        const jsonMatch = trimmedString.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          const jsonResult = JSON.parse(jsonMatch[0]);
          if (
            jsonResult &&
            (jsonResult.data !== undefined ||
              jsonResult.before !== undefined ||
              jsonResult.after !== undefined ||
              jsonResult.operation !== undefined ||
              jsonResult.global_status !== undefined ||
              jsonResult.errors !== undefined)
          ) {
            return jsonResult;
          }
        }
      } catch (e2) {
        // No JSON found in text, continue to text conversion
      }
    }

    // If we get here, it's text output (Axis/TVT/Uniview)
    // Convert to standardized JSON format
    return this.convertTextToJson(trimmedString);
  };

  // Filter out logs and debug messages from text output (deprecated for update-dvr>=2.0.0)
  filterLogsFromText = (textOutput) => {
    if (!textOutput || typeof textOutput !== "string") {
      return textOutput;
    }

    return textOutput
      .split("\n")
      .filter((line) => {
        const trimmed = line.trim();
        // Keep only lines with channel information (numbers, ID Channel, Resolution, etc.)
        // and separator lines (-----------)
        return (
          trimmed &&
          !/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}[,.]\d+\s+-\s+\w+\s+-\s+(INFO|DEBUG|WARNING|ERROR|WARN)\s+-/.test(
            trimmed
          ) &&
          !/^(Port discover|Tentative sur|Open ports|camera_list|No enabled|Port \d+ found|Port \d+ functional|No open port|Device|dvr|display|update|reboot|camera ip|\*\*\*|Camera|Display|Update|Done)/i.test(
            trimmed
          )
        );
      })
      .join("\n");
  };

  // Parse channel information from text output (deprecated for update-dvr>=2.0.0)
  parseChannelsFromText = (filteredText, dvrType) => {
    const channels = { mainstream: [], substream: [] };
    const lines = filteredText.split("\n");
    let currentChannel = null;
    let currentStream = "mainstream"; // Default to mainstream

    lines.forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed) {
        return;
      }

      // Detect separator line (-----------) - save current channel
      if (trimmed === "-----------" || trimmed.match(/^-+$/)) {
        if (currentChannel) {
          channels[currentStream].push(currentChannel);
          currentChannel = null;
        }
        return;
      }

      // Parse channel information based on DVR type
      if (dvrType === "hik" || dvrType === "dahua") {
        // Hikvision/Dahua format: "1", "ID Channel : 101", "Resolution : 960x576", etc.
        if (trimmed.startsWith("ID Channel")) {
          const match = trimmed.match(/ID Channel\s*:\s*(\d+)/i);
          if (match) {
            // Save previous channel if exists
            if (currentChannel) {
              channels[currentStream].push(currentChannel);
            }
            // Start new channel
            const channelId = match[1];
            currentChannel = { id: channelId };
            // Determine stream: channel ID ending in 01 = mainstream, 02 = substream
            const channelIdNum = parseInt(channelId, 10);
            currentStream = channelIdNum % 100 === 1 ? "mainstream" : "substream";
          }
        } else if (trimmed.startsWith("Resolution")) {
          const match = trimmed.match(/Resolution\s*:\s*(\d+)x(\d+)/i);
          if (match && currentChannel) {
            currentChannel.width = parseInt(match[1], 10);
            currentChannel.height = parseInt(match[2], 10);
          }
        } else if (trimmed.match(/Type bande passante|Bitrate Control|Bitrate type/i)) {
          const match = trimmed.match(/:\s*(Constant|VBR|CBR|Variable)/i);
          if (match && currentChannel) {
            currentChannel.bitrate_control = match[1];
          }
        } else if (trimmed.match(/Image par seconde|FPS|Framerate/i)) {
          const match = trimmed.match(/:\s*([\d.]+)/i);
          if (match && currentChannel) {
            currentChannel.fps = parseInt(parseFloat(match[1]), 10);
          }
        } else if (trimmed.match(/Debit binaire|Bitrate|Max bitrate/i)) {
          const match = trimmed.match(/:\s*(\d+)/i);
          if (match && currentChannel) {
            currentChannel.bitrate = parseInt(match[1], 10);
          }
        } else if (trimmed.match(/Video encoding|Video codec|compression/i)) {
          const match = trimmed.match(/:\s*(.+)/i);
          if (match && currentChannel) {
            currentChannel.compression = match[1].trim();
          }
        } else if (trimmed.match(/Motion Detect/i)) {
          const match = trimmed.match(/:\s*(true|false)/i);
          if (match && currentChannel) {
            currentChannel.motion_detect = match[1].toLowerCase() === "true";
          }
        } else if (trimmed.match(/Encrypt/i)) {
          const match = trimmed.match(/:\s*(true|false)/i);
          if (match && currentChannel) {
            currentChannel.encrypt = match[1].toLowerCase() === "true";
          }
        }
      } else if (dvrType === "axis") {
        // Axis format: "ID Channel: 192.168.1.100", "Resolution: 1920x1080", etc.
        if (trimmed.startsWith("ID Channel")) {
          if (currentChannel) {
            channels[currentStream].push(currentChannel);
          }
          const match = trimmed.match(/ID Channel:\s*(.+)/i);
          if (match) {
            currentChannel = { id: match[1].trim() };
          }
        } else if (trimmed.startsWith("Resolution") && currentChannel) {
          const match = trimmed.match(/Resolution:\s*(\d+)x(\d+)/i);
          if (match) {
            currentChannel.width = parseInt(match[1], 10);
            currentChannel.height = parseInt(match[2], 10);
          }
        } else if (trimmed.match(/Frames per second/i) && currentChannel) {
          const match = trimmed.match(/(\d+)\s*fps/i);
          if (match) {
            currentChannel.fps = parseInt(match[1], 10);
          }
        } else if (trimmed.match(/Video encoding/i) && currentChannel) {
          const match = trimmed.match(/Video encoding:\s*(.+)/i);
          if (match) {
            currentChannel.compression = match[1].trim();
          }
        }
      }
    });

    // Don't forget the last channel
    if (currentChannel) {
      channels[currentStream].push(currentChannel);
    }

    return channels;
  };

  // Convert text output to standardized JSON format
  convertTextToJson = (textOutput) => {
    if (!textOutput || typeof textOutput !== "string") {
      return null;
    }

    // Filter out logs and debug messages (deprecated for update-dvr>=2.0.0)
    const filteredText = this.filterLogsFromText(textOutput);

    // Detect operation type from text content
    let operation = "display";
    if (textOutput.toLowerCase().includes("update") || textOutput.toLowerCase().includes("modification")) {
      operation = "update";
    } else if (textOutput.toLowerCase().includes("reboot") || textOutput.toLowerCase().includes("restart")) {
      operation = "reboot";
    }

    // Try to detect DVR type from text
    let dvrType = "unknown";
    const textLower = textOutput.toLowerCase();
    if (textLower.includes("axis")) {
      dvrType = "axis";
    } else if (textLower.includes("tvt") || textLower.includes("ipc.com")) {
      dvrType = "tvt";
    } else if (textLower.includes("uniview") || textLower.includes("lapi")) {
      dvrType = "uniview";
    } else if (textLower.includes("hik") || textLower.includes("hikvision")) {
      dvrType = "hik";
    } else if (textLower.includes("dahua")) {
      dvrType = "dahua";
    }

    // Try to extract device IP from text (look for IP pattern)
    const ipPattern = /\b(?:\d{1,3}\.){3}\d{1,3}\b/;
    const ipMatch = textOutput.match(ipPattern);
    const deviceIp = ipMatch ? ipMatch[0] : "";

    // Determine status based on text content
    let globalStatus = "success";
    if (
      textOutput.toLowerCase().includes("error") ||
      textOutput.toLowerCase().includes("failed") ||
      textOutput.toLowerCase().includes("exception")
    ) {
      globalStatus = "error";
    }

    // Parse channels from filtered text
    const parsedChannels = this.parseChannelsFromText(filteredText, dvrType);

    // Create standardized JSON structure (deprecated for update-dvr>=2.0.0)
    return {
      operation,
      dvr_type: dvrType,
      device_ip: deviceIp,
      data: {
        mainstream: { channels: parsedChannels.mainstream },
        substream: { channels: parsedChannels.substream },
      },
      errors: [],
      global_status: globalStatus,
      message: "",
    };
  };

  formatResolution = (width, height) => {
    if (width && height) {
      return `${width}x${height}`;
    }
    return "N/A";
  };

  getResolutionColor = (resolution) => {
    if (!resolution || resolution === "N/A") return "inherit";
    const width = parseInt(resolution.split("x")[0], 10);
    if (Number.isNaN(width)) return "inherit";
    if (width >= 970 || width < 640) {
      return "red";
    }
    return "green";
  };

  getEncodageColor = (encodageVideo) => {
    if (!encodageVideo || encodageVideo === "N/A") return "inherit";
    if (encodageVideo.toLowerCase().includes("264") || encodageVideo.toLowerCase().includes("h264")) {
      return "green";
    }
    return "red";
  };

  getFpsColor = (fps) => {
    if (!fps || fps === "N/A") return "inherit";
    const fpsNum = parseInt(fps);
    if (Number.isNaN(fpsNum)) return "inherit";
    if (fpsNum > 5 && fpsNum < 16) {
      return "green";
    }
    return "red";
  };

  getBitratesColor = (bitrate) => {
    if (!bitrate || bitrate === "N/A") return "inherit";
    const bitrateNum = parseInt(bitrate);
    if (Number.isNaN(bitrateNum)) return "inherit";
    if (bitrateNum >= 500 && bitrateNum < 1025) {
      return "green";
    }
    return "red";
  };

  getTypebandeColor = () => {
    return "green";
  };

  renderResultHeader = (title, jsonResult) => {
    const statusColorMap = {
      success: "text-success",
      error: "text-danger",
      partial: "text-warning",
    };

    return (
      <Col md="12" className="mb-3">
        <h4 className="text-center">{title}</h4>
        {jsonResult.message && (
          <p className={`text-center ${statusColorMap[jsonResult.global_status] || "text-info"}`}>
            {jsonResult.message}
          </p>
        )}
        {(jsonResult.dvr_type || jsonResult.device_ip) && (
          <p className="text-muted text-center">
            {jsonResult.dvr_type && `DVR Type: ${jsonResult.dvr_type}`}
            {jsonResult.dvr_type && jsonResult.device_ip && " | "}
            {jsonResult.device_ip && `Device IP: ${jsonResult.device_ip}`}
          </p>
        )}
      </Col>
    );
  };

  renderNoChannelsCard = () => (
    <Col md="12">
      <Card className="mb-3">
        <CardBody>
          <p className="text-muted text-center mb-0">No channels found</p>
        </CardBody>
      </Card>
    </Col>
  );

  renderUnknownOperationCard = (operation) => (
    <Col md="12">
      <Card className="border-warning">
        <CardHeader className="bg-warning text-dark">
          <CardTitle tag="h5">Unknown Operation</CardTitle>
        </CardHeader>
        <CardBody>
          <p className="mb-0">Operation: {operation || "unknown"}</p>
        </CardBody>
      </Card>
    </Col>
  );

  extractChannelValues = (channel) => ({
    resolution: this.formatResolution(channel.width, channel.height),
    bitrateControl: channel.bitrate_control,
    fps: channel.fps,
    bitrate: channel.bitrate,
    compression: channel.compression,
    motionDetect: channel.motion_detect,
    encrypt: channel.encrypt,
    dhcp: channel.dhcp,
  });

  renderChannelField = (label, value, color, formatter = (v) => v) => (
    <p>
      <strong>{label}:</strong> <span style={{ color }}>{formatter(value)}</span>
    </p>
  );

  // ========== Error Rendering ==========
  renderErrors = (errors) => {
    if (!errors || errors.length === 0) {
      return null;
    }

    return (
      <Col md="12" className="mb-3">
        <Card className="border-danger">
          <CardHeader className="bg-danger text-white">
            <CardTitle tag="h5">Errors</CardTitle>
          </CardHeader>
          <CardBody>
            {errors.map((error, index) => (
              <div key={index} className="mb-4 pb-3 border-bottom" style={{ borderColor: "#dee2e6" }}>
                {/* Stream + Channel */}
                {(error.channel || error.stream) && (
                  <div className="mb-2">
                    <strong className="text-muted">Stream:</strong> {error.stream || "N/A"} |{" "}
                    <strong className="text-muted">Channel:</strong> {error.channel || "N/A"}
                  </div>
                )}
                {/* Code erreur */}
                {error.code && (
                  <div className="mb-2">
                    <strong className="text-danger">Error Code:</strong> {error.code}
                  </div>
                )}
                {/* Message */}
                <div className="mb-2">
                  <strong className="text-muted">Message:</strong> {error.message || "No message available"}
                </div>
                {/* Details (optionnel) */}
                {error.details && (
                  <div className="small text-muted mt-2">
                    <strong>Details:</strong> {error.details}
                  </div>
                )}
              </div>
            ))}
          </CardBody>
        </Card>
      </Col>
    );
  };

  // ========== Camera Card Rendering ==========
  renderCameraCard = (channel, forceWhite = false) => {
    const values = this.extractChannelValues(channel);
    const color = forceWhite ? "inherit" : null;

    return (
      <Card key={channel.id} className="mb-2">
        <CardHeader>
          <CardTitle className="text-center" tag="h5">
            Channel {channel.id}
          </CardTitle>
        </CardHeader>
        <CardBody>
          {this.renderChannelField(
            "Resolution",
            values.resolution,
            color || this.getResolutionColor(values.resolution)
          )}
          {this.renderChannelField(
            "Bitrate Control",
            values.bitrateControl || "N/A",
            color || this.getTypebandeColor()
          )}
          {this.renderChannelField("FPS", values.fps || "N/A", color || this.getFpsColor(values.fps))}
          {this.renderChannelField(
            "Bitrate",
            values.bitrate || "N/A",
            color || this.getBitratesColor(values.bitrate),
            (v) => {
              return v === "N/A" ? "N/A" : `${v} kbps`;
            }
          )}
          {this.renderChannelField(
            "Video Encoding",
            values.compression || "N/A",
            color || this.getEncodageColor(values.compression)
          )}
          {values.dhcp && (
            <p>
              <strong>DHCP:</strong> <span style={{ color: color || "inherit" }}>{values.dhcp}</span>
            </p>
          )}
          {values.motionDetect !== undefined && (
            <p>
              <strong>Motion Detect:</strong>{" "}
              <span style={{ color: color || "inherit" }}>{String(values.motionDetect)}</span>
            </p>
          )}
          {values.encrypt !== undefined && (
            <p>
              <strong>Encrypt:</strong> <span style={{ color: color || "inherit" }}>{String(values.encrypt)}</span>
            </p>
          )}
        </CardBody>
      </Card>
    );
  };

  renderUpdatedCameraCard = (oldChannel, newChannel) => {
    const hasChanged = (field) => {
      if (oldChannel[field] === undefined && newChannel[field] === undefined) return false;
      return oldChannel[field] !== newChannel[field];
    };

    const oldValues = this.extractChannelValues(oldChannel);
    const newValues = this.extractChannelValues(newChannel);

    const getChangeColor = (oldVal, newVal, field) => {
      if (field === "resolution") {
        return oldVal !== newVal || hasChanged("width") || hasChanged("height") ? "green" : "inherit";
      }
      return oldVal !== newVal || hasChanged(field) ? "green" : "inherit";
    };

    return (
      <Card key={newChannel.id} className="mb-2">
        <CardHeader>
          <CardTitle className="text-center" tag="h5">
            Channel {newChannel.id}
          </CardTitle>
        </CardHeader>
        <CardBody>
          {this.renderChannelField(
            "Resolution",
            newValues.resolution,
            getChangeColor(oldValues.resolution, newValues.resolution, "resolution")
          )}
          {this.renderChannelField(
            "Bitrate Control",
            newValues.bitrateControl || "N/A",
            getChangeColor(oldValues.bitrateControl, newValues.bitrateControl, "bitrate_control")
          )}
          {this.renderChannelField("FPS", newValues.fps || "N/A", getChangeColor(oldValues.fps, newValues.fps, "fps"))}
          {this.renderChannelField(
            "Bitrate",
            newValues.bitrate || "N/A",
            getChangeColor(oldValues.bitrate, newValues.bitrate, "bitrate"),
            (v) => {
              return v === "N/A" ? "N/A" : `${v} kbps`;
            }
          )}
          {this.renderChannelField(
            "Video Encoding",
            newValues.compression || "N/A",
            getChangeColor(oldValues.compression, newValues.compression, "compression")
          )}
          {newChannel.dhcp !== undefined && (
            <p>
              <strong>DHCP:</strong>{" "}
              <span style={{ color: hasChanged("dhcp") ? "green" : "inherit" }}>{newChannel.dhcp || "N/A"}</span>
            </p>
          )}
          {newValues.motionDetect !== undefined && (
            <p>
              <strong>Motion Detect:</strong>{" "}
              <span
                style={{
                  color: getChangeColor(oldValues.motionDetect, newValues.motionDetect, "motion_detect"),
                }}
              >
                {String(newValues.motionDetect)}
              </span>
            </p>
          )}
          {newValues.encrypt !== undefined && (
            <p>
              <strong>Encrypt:</strong>{" "}
              <span style={{ color: getChangeColor(oldValues.encrypt, newValues.encrypt, "encrypt") }}>
                {String(newValues.encrypt)}
              </span>
            </p>
          )}
        </CardBody>
      </Card>
    );
  };

  renderCameraCards = (mainChannels, subChannels) => {
    const cards = [];
    const maxLength = Math.max(mainChannels.length, subChannels.length);

    for (let i = 0; i < maxLength; i++) {
      cards.push(
        <Row key={i} className="mb-4">
          {mainChannels[i] && <Col md="6">{this.renderCameraCard(mainChannels[i])}</Col>}
          {subChannels[i] && <Col md="6">{this.renderCameraCard(subChannels[i])}</Col>}
        </Row>
      );
    }
    return cards;
  };

  renderUpdatedCameraCards = (beforeChannels, afterChannels) => {
    const beforeMap = new Map();
    beforeChannels.forEach((ch) => beforeMap.set(ch.id, ch));

    const beforeCards = [];
    const afterCards = [];

    afterChannels.forEach((afterChannel) => {
      const beforeChannel = beforeMap.get(afterChannel.id);
      if (beforeChannel) {
        beforeCards.push(this.renderCameraCard(beforeChannel, true));
        afterCards.push(this.renderUpdatedCameraCard(beforeChannel, afterChannel));
      } else {
        beforeCards.push(
          <Card key={`before-${afterChannel.id}`} className="mb-2">
            <CardBody>
              <p className="text-muted">Channel {afterChannel.id} (new)</p>
            </CardBody>
          </Card>
        );
        afterCards.push(this.renderCameraCard(afterChannel));
      }
    });

    beforeChannels.forEach((beforeChannel) => {
      if (!afterChannels.find((ch) => ch.id === beforeChannel.id)) {
        beforeCards.push(this.renderCameraCard(beforeChannel, true));
        afterCards.push(
          <Card key={`removed-${beforeChannel.id}`} className="mb-2 border-danger">
            <CardBody>
              <p className="text-danger">Channel {beforeChannel.id} (removed)</p>
            </CardBody>
          </Card>
        );
      }
    });

    const maxLength = Math.max(beforeCards.length, afterCards.length);
    const rows = [];
    for (let i = 0; i < maxLength; i++) {
      rows.push(
        <Row key={i} className="mb-4">
          <Col md="6">{beforeCards[i]}</Col>
          <Col md="6">{afterCards[i]}</Col>
        </Row>
      );
    }

    return (
      <div>
        <Row>
          <Col md="6">
            <h6 className="text-muted mb-2">Before</h6>
          </Col>
          <Col md="6">
            <h6 className="text-muted mb-2">
              After{" "}
              <span className="small text-lowercase">
                (changes in <span style={{ color: "green" }}>green</span>)
              </span>
            </h6>
          </Col>
        </Row>
        {rows}
      </div>
    );
  };

  // ========== Operation-Specific Rendering ==========
  renderDisplay = (jsonResult) => {
    const mainChannels = jsonResult.data?.mainstream?.channels || [];
    const subChannels = jsonResult.data?.substream?.channels || [];

    return (
      <div>
        {this.renderResultHeader("DVR Display Result", jsonResult)}
        {mainChannels.length === 0 && subChannels.length === 0 && this.renderNoChannelsCard()}
        {this.renderCameraCards(mainChannels, subChannels)}
        {this.renderErrors(jsonResult.errors)}
      </div>
    );
  };

  renderUpdate = (jsonResult) => {
    if (jsonResult.global_status === "error") {
      return (
        <div>
          {this.renderResultHeader("DVR Update Result", jsonResult)}
          {this.renderErrors(jsonResult.errors)}
        </div>
      );
    }

    const mainChannelsBefore = jsonResult.before?.mainstream?.channels || [];
    const subChannelsBefore = jsonResult.before?.substream?.channels || [];
    const mainChannelsAfter = jsonResult.after?.mainstream?.channels || [];
    const subChannelsAfter = jsonResult.after?.substream?.channels || [];

    const allMainBefore = [...mainChannelsBefore];
    const allMainAfter = [...mainChannelsAfter];
    const allSubBefore = [...subChannelsBefore];
    const allSubAfter = [...subChannelsAfter];

    const hasNoChannels =
      allMainBefore.length === 0 && allMainAfter.length === 0 && allSubBefore.length === 0 && allSubAfter.length === 0;

    return (
      <div>
        {this.renderResultHeader("DVR Update Result", jsonResult)}
        {hasNoChannels && this.renderNoChannelsCard()}
        {allMainBefore.length > 0 || allMainAfter.length > 0 ? (
          <Col md="12" className="mb-4">
            <h5 className="mb-3">Main Stream</h5>
            {this.renderUpdatedCameraCards(allMainBefore, allMainAfter)}
          </Col>
        ) : null}
        {allSubBefore.length > 0 || allSubAfter.length > 0 ? (
          <Col md="12" className="mb-4">
            <h5 className="mb-3">Sub Stream</h5>
            {this.renderUpdatedCameraCards(allSubBefore, allSubAfter)}
          </Col>
        ) : null}
        {this.renderErrors(jsonResult.errors)}
      </div>
    );
  };

  renderReboot = (jsonResult) => {
    return (
      <div>
        {this.renderResultHeader("DVR Reboot Result", jsonResult)}
        {this.renderErrors(jsonResult.errors)}
        {jsonResult.global_status === "success" && (
          <Col md="12" className="mt-3">
            <Card className="border-success">
              <CardHeader className="bg-success text-white">
                <CardTitle tag="h5">Reboot Successful</CardTitle>
              </CardHeader>
              <CardBody>
                <p className="mb-0">The DVR has been rebooted successfully.</p>
              </CardBody>
            </Card>
          </Col>
        )}
      </div>
    );
  };

  renderOperationResult = (jsonResult) => {
    switch (jsonResult.operation) {
      case "display":
        return this.renderDisplay(jsonResult);
      case "update":
        return this.renderUpdate(jsonResult);
      case "reboot":
        return this.renderReboot(jsonResult);
      default:
        return <div>{this.renderUnknownOperationCard(jsonResult.operation)}</div>;
    }
  };

  // ========== Main Render ==========
  render() {
    const { stdOut, stdErr, status } = this.state;

    // Check for stderr errors first (e.g., command not found)
    if (stdErr && typeof stdErr === "string" && stdErr.trim()) {
      const stderrLower = stdErr.toLowerCase();
      if (stderrLower.includes("command not found")) {
        return (
          <div>
            <Col md="12">
              <Card className="border-danger">
                <CardHeader className="bg-danger text-white">
                  <CardTitle tag="h5">Error</CardTitle>
                </CardHeader>
                <CardBody>
                  <p className="text-danger">Update-dvr library not installed on the server</p>
                  <pre className="p-3 rounded mt-3">{stdErr}</pre>
                </CardBody>
              </Card>
            </Col>
          </div>
        );
      }
    }

    if (typeof stdOut === "object" && stdOut && stdOut.status) {
      if (stdOut.status === "running") {
        return (
          <div>
            <Col md="12">
              <h4 className="text-center">DVR Update Operation</h4>
              <p className="text-info text-center">{stdOut.message}</p>
              <p className="text-muted text-center">Operation ID: {stdOut.operation_id}</p>
              <p className="text-muted text-center">DVR Type: {stdOut.dvr_type}</p>
              <p className="text-muted text-center">Device IP: {stdOut.device_ip}</p>
            </Col>
          </div>
        );
      }

      if (stdOut.status === "success") {
        const { result } = stdOut;

        let jsonResult = null;
        if (typeof result === "string") {
          jsonResult = this.parseJsonResult(result);
          // If parseJsonResult returns null, try convertTextToJson as fallback
          if (!jsonResult) {
            jsonResult = this.convertTextToJson(result);
          }
        } else if (result && typeof result === "object") {
          jsonResult = result;
        }

        if (!jsonResult) {
          return (
            <div>
              <Col md="12">
                <Card className="border-warning">
                  <CardHeader className="bg-warning text-dark">
                    <CardTitle tag="h5">Invalid Response</CardTitle>
                  </CardHeader>
                  <CardBody>
                    <p>Failed to parse DVR result. Raw output:</p>
                    <pre
                      className="p-3 rounded"
                      style={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        maxHeight: "500px",
                        overflow: "auto",
                      }}
                    >
                      {typeof result === "string" ? result : JSON.stringify(result, null, 2)}
                    </pre>
                    {stdErr && (
                      <div className="mt-3">
                        <strong className="text-danger">Error output:</strong>
                        <pre className="p-3 rounded mt-2">{stdErr}</pre>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </div>
          );
        }

        return this.renderOperationResult(jsonResult);
      }

      if (stdOut.status === "failed") {
        return (
          <div>
            <Col md="12">
              <h4 className="text-center">DVR Update Failed</h4>
              <p className="text-danger text-center">{stdOut.message}</p>
              {stdOut.stdout && <pre className="text-left mt-3 p-3 rounded">{stdOut.stdout}</pre>}
              {stdOut.stderr && (
                <div className="mt-3">
                  <strong className="text-danger">Error output:</strong>
                  <pre className="text-left mt-2 p-3 rounded">{stdOut.stderr}</pre>
                </div>
              )}
            </Col>
          </div>
        );
      }
    }

    // Handle status === "FAILED" with stderr
    if (status === "FAILED" && stdErr) {
      return (
        <div>
          <Col md="12">
            <Card className="border-danger">
              <CardHeader className="bg-danger text-white">
                <CardTitle tag="h5">DVR Update Failed</CardTitle>
              </CardHeader>
              <CardBody>
                <p className="text-danger">The operation failed with the following error:</p>
                <pre className="p-3 rounded">{stdErr}</pre>
                {stdOut && (
                  <div className="mt-3">
                    <strong>Standard output:</strong>
                    <pre className="p-3 rounded mt-2">
                      {typeof stdOut === "string" ? stdOut : JSON.stringify(stdOut, null, 2)}
                    </pre>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </div>
      );
    }

    // Try to parse as JSON (even if it doesn't start with {)
    if (typeof stdOut === "string" && stdOut.trim()) {
      const jsonResult = this.parseJsonResult(stdOut);
      if (jsonResult) {
        return this.renderOperationResult(jsonResult);
      }
    }

    // If we have output but couldn't parse it, try to convert text to JSON
    if (stdOut) {
      const textOutput = typeof stdOut === "string" ? stdOut : JSON.stringify(stdOut, null, 2);
      const jsonResult = this.convertTextToJson(textOutput);
      if (jsonResult) {
        return this.renderOperationResult(jsonResult);
      }

      // Fallback: display raw output
      return (
        <Row>
          <Col md="12">
            <Card className="border-info">
              <CardHeader>
                <CardTitle tag="h5">Raw Output</CardTitle>
              </CardHeader>
              <CardBody>
                <pre
                  className="p-3 rounded"
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    maxHeight: "500px",
                    overflow: "auto",
                  }}
                >
                  {textOutput}
                </pre>
                {stdErr && (
                  <div className="mt-3">
                    <strong className="text-danger">Error output:</strong>
                    <pre className="p-3 rounded mt-2">{stdErr}</pre>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      );
    }

    // No output
    return (
      <div>
        <Col md="12">
          <p className="text-muted text-center">No output available</p>
          {stdErr && (
            <div className="mt-3">
              <Card className="border-warning">
                <CardHeader className="bg-warning text-dark">
                  <CardTitle tag="h5">Warning</CardTitle>
                </CardHeader>
                <CardBody>
                  <pre className="p-3 rounded">{stdErr}</pre>
                </CardBody>
              </Card>
            </div>
          )}
        </Col>
      </div>
    );
  }
}

export default UpdatedvrResult;
